import { format, getWeek, Locale } from 'date-fns';
import { FC, ReactNode } from 'react';

import { StorePerformanceInterval } from '../../../../constants';
import { Icon, Icons, Typography } from '../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../stories/atoms/containerV2/ContainerV2';
import { Colors } from '../../../../theme';
import { InfoTooltipIcon } from '../infoTooltipIcon/InfoTooltipIcon';
import { StyledTrendIconComponent } from '../styledTrendIcon/StyledTrendIcon';
import { isMonthlyView, isWeeklyView } from '../../util';

type BaseCardProps = {
  backgroundColor: Colors;
  tooltipText?: string;
  tooltipTestId: string;
};

type CurrentPeriodCardProps = BaseCardProps & {
  value: ReactNode;
  timeFrame: string;
};

type TrendCardProps = BaseCardProps & {
  originalAmount: number;
  previousAmount: number;
  label: string;
  hideTrendText?: boolean;
  valueFormatter: (value: number) => string;
};

export const getWidgetTimeFrameLabel = (
  interval: StorePerformanceInterval,
  currentDate: Date,
  weekLabel: string,
  locale?: Locale,
): string => {
  if (isMonthlyView(interval)) {
    return format(currentDate, 'MMM yyyy', { locale });
  }

  if (isWeeklyView(interval)) {
    return `${weekLabel} ${getWeek(currentDate, { locale })}`;
  }

  return format(currentDate, 'dd MMM yyyy', { locale });
};

export const CurrentPeriodCard: FC<CurrentPeriodCardProps> = ({
  backgroundColor,
  tooltipText,
  tooltipTestId,
  value,
  timeFrame,
}) => (
  <ColumnFlexContainer
    padding={[2, 0]}
    backgroundColor={backgroundColor}
    borderRadius={[2]}
    shadow
    flex={1}
    alignItems="center"
  >
    <RowFlexContainer width="100%" justifyContent="end" padding={[0, 2, 1, 0]}>
      <InfoTooltipIcon tooltipText={tooltipText} testId={tooltipTestId} />
    </RowFlexContainer>

    <Typography type="button" margin={[0, 0, 3, 0]}>
      {value}
    </Typography>
    <Icon margin={[0, 0, 2, 0]} icon={Icons.STORE} />
    <Typography>{timeFrame}</Typography>
  </ColumnFlexContainer>
);

export const TrendCard: FC<TrendCardProps> = ({
  originalAmount,
  previousAmount,
  label,
  backgroundColor,
  hideTrendText = false,
  tooltipText,
  tooltipTestId,
  valueFormatter,
}) => (
  <ColumnFlexContainer
    padding={[2, 0]}
    backgroundColor={backgroundColor}
    borderRadius={[2]}
    shadow
    flex={1}
    alignItems="center"
  >
    <RowFlexContainer width="100%" justifyContent="end" padding={[0, 2, 1, 0]}>
      <InfoTooltipIcon tooltipText={tooltipText} testId={tooltipTestId} />
    </RowFlexContainer>

    <StyledTrendIconComponent
      originalAmount={originalAmount}
      previousAmount={previousAmount}
      textOptions={{ margin: [0, 0, 3, 0] }}
      valueFormatter={valueFormatter}
      hideTrendText={hideTrendText}
    />
    <Typography>{label}</Typography>
  </ColumnFlexContainer>
);
